import { Controller } from "@hotwired/stimulus";

// CopyLink — replica el comportamiento de CopyLink.tsx.
// Copia una URL al portapapeles y muestra feedback: swap del label
// "Copiar" → "Copiado" en el boton + flash toast en pantalla.
//
// El helper Rails `copy_link()` genera el HTML correcto. NO escribir
// HTML inline con estos data attributes — usar el helper.

export default class extends Controller {
  static targets = ["label"];
  static values = {
    url: String,
    copied: { type: String, default: "Copiado" },
  };

  copy() {
    const text = this.urlValue;
    if (!text) return;

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text)
        .then(() => this.showCopied())
        .catch(() => this.#legacyCopy(text));
    } else {
      this.#legacyCopy(text);
    }
  }

  #legacyCopy(text) {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.cssText = "position:fixed;left:-9999px;opacity:0;pointer-events:none";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    let ok = false;
    try { ok = document.execCommand("copy"); } catch (_) { /* noop */ }
    ta.remove();
    if (ok) this.showCopied();
  }

  openUrl() {
    window.open(this.urlValue, "_blank");
  }

  showCopied() {
    // El swap de icono `fa-copy` → `fa-check` no funciona porque FontAwesome
    // corre con `dom.watch()` (modo SVG-con-JS) y convierte cada `<i>` en
    // `<svg>` cuyo `className` es un `SVGAnimatedString` sin `.replace()`.
    // Aunque cambiaramos el atributo class, el path SVG ya esta baked-in.
    // Por eso solo se intercambia el texto del label, y el feedback principal
    // es el flash toast.
    if (this.hasLabelTarget) {
      if (!this._originalLabelText) {
        this._originalLabelText = this.labelTarget.textContent;
      }
      this.labelTarget.textContent = this.copiedValue;

      clearTimeout(this._timer);
      this._timer = setTimeout(() => {
        this.labelTarget.textContent = this._originalLabelText;
      }, 2000);
    }

    this.#showFlash(this.copiedValue);
  }

  #showFlash(message) {
    // Remueve cualquier flash de copia previo (evita stacking)
    document.querySelectorAll("[data-copy-link-flash]").forEach((el) => el.remove());

    const wrapper = document.createElement("div");
    wrapper.setAttribute("data-copy-link-flash", "");
    wrapper.className = "fixed top-0 left-0 right-0 p-4 flex justify-center pointer-events-none";
    wrapper.style.zIndex = "100000";
    wrapper.style.opacity = "0";
    wrapper.style.transform = "translateY(-20px)";
    wrapper.style.transition = "opacity 300ms ease-in-out, transform 300ms ease-in-out";

    const inner = document.createElement("div");
    inner.className = "max-w-[500px] bg-black text-white py-[8px] px-[12px] rounded-md shadow-lg inline-flex items-center gap-[8px] text-[14px] font-medium pointer-events-auto";

    const icon = document.createElement("i");
    icon.className = "fa-solid fa-circle-check text-green-500";

    const text = document.createElement("p");
    text.textContent = message;

    inner.append(icon, text);
    wrapper.appendChild(inner);
    document.body.appendChild(wrapper);

    // Animacion de entrada
    requestAnimationFrame(() => {
      wrapper.style.opacity = "1";
      wrapper.style.transform = "translateY(0)";
    });

    // Auto-dismiss tras 2.5s
    setTimeout(() => {
      wrapper.style.opacity = "0";
      wrapper.style.transform = "translateY(-20px)";
      setTimeout(() => wrapper.remove(), 300);
    }, 2500);
  }

  disconnect() {
    clearTimeout(this._timer);
  }
}
